import { useState } from 'react';
import { useUserPageContextValueHook } from '@/contexts/UserPageContextProvider';
import { useLinkCategoryContextValueHook } from '@/views/mypage/contexts/LinkCategoryContextProvider';
import { useQueryParamsHook } from '@/hooks/useQueryParamsHook';
import { useMyPostsQuery } from '@/hooks/ReactQuery/PostReactQuery/PostReactQuery';

export const POST_LIST_LAYOUTS = [
    { value: 'CARD', label: '카드', description: '커버·요약까지' },
    { value: 'LIST', label: '목록', description: '제목·날짜만' },
];
export const POST_LIST_ORDERS = [
    { value: 'NEWEST', label: '최신 이야기부터', description: '일기·소식' },
    { value: 'OLDEST', label: '첫 이야기부터', description: '소설 연재 목차' },
];
export const POST_LIST_LIMITS = [3, 5, 10];

const OPTION = 'flex flex-col items-start rounded-xl px-3 py-2.5 text-left ring-1 transition';

/**
 * 글 목록 모듈 입력 폼 (추가·수정 공용). 글 자체는 "글" 탭에서 쓴다.
 * onSubmit 에는 서버 link-items/create, update/info 에 보낼 body(id 제외)를 넘긴다.
 */
export function WegetPostListModuleForm({ initial, submitLabel, onSubmit }) {
    const userPageId = useUserPageContextValueHook()?.userPage?.id;
    const categories = useLinkCategoryContextValueHook()?.content || [];
    const queryParamsHook = useQueryParamsHook();
    const lcId = queryParamsHook?.params?.lcId;
    const firstPage = useMyPostsQuery({ userPageId })?.data?.pages?.[0];
    const groups = firstPage?.groups || [];
    const config = initial?.postListConfig;

    const [title, setTitle] = useState(initial?.title || '');
    const [group, setGroup] = useState(config?.group || '');
    const [layout, setLayout] = useState(config?.layout || 'CARD');
    const [order, setOrder] = useState(config?.order || 'NEWEST');
    const [limit, setLimit] = useState(config?.limit || 5);
    const [linkCategoryId, setLinkCategoryId] = useState(
        categories.find(r => r.id === (initial ? initial.linkCategoryId : lcId))?.id || ''
    );

    // 지금 고른 그룹이 지워졌어도 선택지에 남긴다
    const groupOptions = group && !groups.some(g => g.name === group) ? [...groups, { name: group, count: 0 }] : groups;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            type: 'POSTS',
            title: title.trim(),
            postListConfig: { group: group || null, layout, order, limit },
            linkCategoryId: linkCategoryId || '',
            userPageId,
        });
    }

    const choice = (selected, value, onChange, option) => (
        <button
            key={option.value}
            type="button"
            aria-pressed={selected === option.value}
            onClick={() => onChange(option.value)}
            className={`${OPTION} ${selected === option.value ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-800 ring-gray-200 hover:bg-gray-50'}`}
        >
            <span className="text-sm font-bold">{option.label}</span>
            <span className={`text-[11px] ${selected === option.value ? 'text-white/70' : 'text-gray-500'}`}>{option.description}</span>
        </button>
    );

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-1 pb-2">
            {firstPage && firstPage.total === 0 &&
                <div className="rounded-xl bg-[var(--mainColor100)] px-4 py-3 text-[13px] text-gray-700">
                    아직 쓴 이야기가 없어요. 모듈을 먼저 넣어 두고 <button type="button" onClick={() => queryParamsHook.setSearchParams({ tabType: 'POSTS', postId: 'new' })} className="font-bold underline">이야기 탭에서 첫 이야기</button>를 써도 돼요. 발행한 이야기가 생기면 페이지에 나타나요.
                </div>
            }

            <label className="flex flex-col gap-1">
                <span className="text-sm font-bold">제목 <span className="font-normal text-gray-400">선택 · 목록 위에 표시</span></span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={50} placeholder="예: 일기, 연재 소설, 자기소개" className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-gray-900" />
            </label>

            <label className="flex flex-col gap-1">
                <span className="text-sm font-bold">보여 줄 이야기</span>
                <select value={group} onChange={(e) => setGroup(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-gray-900">
                    <option value="">전체 이야기</option>
                    {groupOptions.map(g => <option key={g.name} value={g.name}>그룹: {g.name}</option>)}
                </select>
                {groups.length === 0 && <span className="text-[12px] text-gray-500">이야기를 쓸 때 그룹(예: 일기, 소설)을 정하면 그룹별로 나눠 보여 줄 수 있어요.</span>}
            </label>

            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-bold">보기 방식</span>
                <div className="grid grid-cols-2 gap-2">{POST_LIST_LAYOUTS.map(o => choice(layout, o.value, setLayout, o))}</div>
            </div>

            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-bold">순서</span>
                <div className="grid grid-cols-2 gap-2">{POST_LIST_ORDERS.map(o => choice(order, o.value, setOrder, o))}</div>
            </div>

            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-bold">처음에 보여 줄 이야기 수 <span className="font-normal text-gray-400">나머지는 더 보기</span></span>
                <div className="flex gap-2">
                    {POST_LIST_LIMITS.map(n => (
                        <button key={n} type="button" aria-pressed={limit === n} onClick={() => setLimit(n)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ring-1 ${limit === n ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`}>
                            {n}개
                        </button>
                    ))}
                </div>
            </div>

            {categories.length > 0 &&
                <label className="flex flex-col gap-1">
                    <span className="text-sm font-bold">카테고리</span>
                    <select value={linkCategoryId} onChange={(e) => setLinkCategoryId(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-gray-900">
                        <option value="">전체</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
            }

            <button type="submit" className="rounded-full bg-gray-900 py-3.5 text-[15px] font-bold text-white hover:bg-gray-700">
                {submitLabel}
            </button>
        </form>
    );
}
